import { useMsal } from '@azure/msal-react';
import { CircularProgress, styled } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { fetchTokens, login, loginAzureAD, logoutUserService } from '../../services/auth';
import { findUser } from '../../services/users';
import { UserRole } from '../../Types/enums';
import { User } from '../../Types/user';

const SpinnerContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
});

interface AuthContextType {
  currentUser: User | null;
  setCurrentUser: Dispatch<SetStateAction<User | null>>;
  userFullName: string;
  isAdmin: boolean;
  isDealGPTAdmin: boolean;
  authenticate: (email: string, password: string, redirectTo: string) => void;
  authenticateAzureAD: (redirectTo: string) => void;
  fetchTokens: () => Promise<void>;
  fetchProfilePhoto: () => Promise<void>;
  logout: (redirect?: string) => Promise<void>;
  initialized: boolean;
  refetchUser: (userId: string) => Promise<void>;
  refreshTokens: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface Props {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<Props> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userFullName, setUserFullName] = useState('');
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [checkedForToken, setCheckedForToken] = useState(false);

  const navigate = useNavigate();
  const { instance } = useMsal();

  const isAdmin = useMemo(
    () => currentUser?.roles.includes(UserRole.ADMIN) || false,
    [currentUser]
  );

  const isDealGPTAdmin = useMemo(
    () => currentUser?.roles.includes(UserRole.DEALGPT_ADMIN) || false,
    [currentUser]
  );

  const logout = useCallback(
    async (redirectTo?: string) => {
      try {
        await logoutUserService();
        setCurrentUser(null);
        if (redirectTo) {
          navigate(redirectTo, { replace: true });
        }
      } catch (error) {
        setCurrentUser(null);
        enqueueSnackbar('Error logging out', { variant: 'error' });
      }
    },
    [navigate]
  );

  async function fetchProfilePhoto(retryCount = 1) {
    try {
      const profilePhotoData = await fetch(
        'https://graph.microsoft.com/v1.0/me/photos/48x48/$value',
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('graphToken')}`,
          },
        }
      );

      if (profilePhotoData.status === 200) {
        const photoData = await profilePhotoData.blob();
        localStorage.setItem('profilePhoto', URL.createObjectURL(photoData));
        window.dispatchEvent(new Event('storage'));
      } else {
        throw new Error('Failed to fetch profile photo');
      }
    } catch (error) {
      if (retryCount > 0) {
        await fetchTokens();
        await fetchProfilePhoto(retryCount - 1);
      } else {
        localStorage.setItem('profilePhoto', '');
        window.dispatchEvent(new Event('storage'));
      }
    }
  }

  useEffect(() => {
    const tokenLogin = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const token = searchParams.get('token');
      if (token) {
        localStorage.setItem('jwt', token);
        setCheckedForToken(true);
      } else {
        setCheckedForToken(true);
      }
    };
    tokenLogin();
  }, []);

  const refreshTokens = useCallback(async (): Promise<boolean> => {
    try {
      const account = instance.getAllAccounts()[0];
      if (!account) {
        console.error('No account found for token refresh');
        return false;
      }

      // Acquire new Azure AD tokens
      const token = await instance.acquireTokenSilent({
        scopes: [`api://${import.meta.env.VITE_AZUREAD_CLIENT_ID}/dev/app.dev`],
        account,
      });

      const graphToken = await instance.acquireTokenSilent({
        scopes: ['Sites.Read.All'],
        account: instance.getAllAccounts()[0],
      });

      // Store Azure AD tokens
      localStorage.setItem('token', token.accessToken);
      localStorage.setItem('graphToken', graphToken.accessToken);

      // Get new JWT from backend
      const user = await loginAzureAD();
      setCurrentUser(user);
      if (user.token) {
        localStorage.setItem('jwt', user.token);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, [instance]);

  useEffect(() => {
    const checkLogin = async () => {
      try {
        let user = null;
        try {
          user = await login();
        } catch (error) {
          console.error(error);
        }
        if (user) {
          setCurrentUser(user);
        } else {
          setInitialized(false);
          await instance.initialize();
          const authResult = await instance.handleRedirectPromise();

          await refreshTokens();

          if (authResult?.state) {
            window.location.href = authResult.state;
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        await fetchProfilePhoto();
        setLoading(false);
        setInitialized(true);
        setUserFullName(instance.getAllAccounts()[0]?.name || '');
      }
    };
    if (checkedForToken) {
      checkLogin();
    }

    // Including the navigate dep causes an infinite loop, but using react router to navigate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, checkedForToken]);

  const refetchUser = useCallback(async (userId: string) => {
    try {
      if (!userId) return;
      const user = await findUser(userId);
      setCurrentUser(user);
    } catch (error) {
      console.error('Error refetching user:', error);
    }
  }, []);

  const authenticate = async (email: string, password: string, redirectTo: string) => {
    const user = await login(email, password);
    setCurrentUser(user);
    navigate(redirectTo);
  };

  const authenticateAzureAD = async (redirectTo: string) => {
    await instance.loginRedirect({
      scopes: [`api://${import.meta.env.VITE_AZUREAD_CLIENT_ID}/dev/app.dev`],
      state: redirectTo,
    });
  };

  if (!initialized || loading) {
    return (
      <SpinnerContainer>
        <CircularProgress />
      </SpinnerContainer>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        userFullName,
        authenticate,
        authenticateAzureAD,
        logout,
        fetchTokens,
        fetchProfilePhoto,
        initialized,
        isAdmin,
        isDealGPTAdmin,
        refetchUser,
        refreshTokens,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return authContext;
};

export const RequireAuth: React.FC<Props> = ({ children }) => {
  const { currentUser, initialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (initialized && !currentUser) {
      // Handle edge case where a user logged out while viewing a conversation, truncate the
      // url to only be chat.
      let pathname = location.pathname;

      if (pathname.endsWith('/new-chat')) {
        pathname = pathname.replace('/new-chat', '');
      }
      navigate(`/login?redirect=${pathname}${location.search}`, { replace: true });
    }
  }, [currentUser, initialized, navigate, location]);

  if (!initialized) {
    return <CircularProgress />;
  }

  return <>{children}</>;
};

export const NigelProtections: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.origin !== import.meta.env.VITE_AZUREAD_REDIRECT_URI) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  return (
    <>
      <RequireAuth>{children}</RequireAuth>
    </>
  );
};

export const ExpertProtections: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.origin !== import.meta.env.VITE_EXPERTS_REDIRECT_ORIGIN_URI) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  return (
    <>
      <RequireAuth>{children}</RequireAuth>
    </>
  );
};
